import { useEffect, useState } from "react";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useTilesViewAPI } from "@/app/hooks/api_hooks/Tiles/useTilesViewAPI";
import { ITilesViewResponseData } from "@/api_framework/api_modals/Tiles";

const capitalizeFirstLetter = (value?: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const DetailSection: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-4">
    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">{label}</p>
    {children}
  </div>
);

const ViewTileModalContent: React.FC<{ tile: ITilesViewResponseData }> = ({ tile }) => {
  return (
    <div className="mt-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-5 bg-gray-50 rounded-md p-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {tile.type === "service" ? "Match Key" : "Collection ID"}
          </p>
          <p className="text-sm font-medium text-gray-900 mt-0.5">
            {(tile.type === "service" ? tile.match_key : tile.collection_id) || "—"}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Type</p>
          <p className="text-sm font-medium text-gray-900 mt-0.5 capitalize">{tile.type || "—"}</p>
        </div>
      </div>

      <DetailSection label="Persona">
        {tile.persona?.length ? (
          <div className="flex flex-col gap-1.5">
            {tile.persona.map((persona, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-3 py-2"
              >
                <span className="text-sm font-medium text-gray-900 truncate">{persona}</span>
                <span className="flex-shrink-0 text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
                  Sorting key: {tile.sorting_key?.[persona] ?? "—"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">All</p>
        )}
      </DetailSection>

      {tile.ribbon?.length > 0 && (
        <DetailSection label="Ribbon">
          <div className="flex flex-col gap-2.5">
            {tile.ribbon.map((ribbon, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-3">
                <span
                  className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full mb-2 border"
                  style={{
                    backgroundColor: ribbon.chip_color || "#FFE0F7FA",
                    color: "#008a1d",
                    borderColor: ribbon.chip_border_color || "transparent",
                  }}
                >
                  {ribbon.chip_txt || "—"}
                </span>
                <div className="flex items-baseline gap-1.5 text-sm">
                  <span className="text-gray-500 flex-shrink-0">Persona:</span>
                  <span className="text-gray-900 font-medium truncate">
                    {ribbon.persona?.length ? ribbon.persona.join(", ") : "All"}
                  </span>
                </div>
                {ribbon.exclude_persona?.length > 0 && (
                  <div className="flex items-baseline gap-1.5 text-sm mt-1">
                    <span className="text-gray-500 flex-shrink-0">Exclude:</span>
                    <span className="text-gray-900 font-medium truncate">{ribbon.exclude_persona.join(", ")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DetailSection>
      )}
    </div>
  );
};

const ViewTileModal = () => {
  const { setInvisible } = useModalActions("TILE_VIEW_MODAL");
  const isVisible = useModal("TILE_VIEW_MODAL");
  const tileName = useModalState("TILE_VIEW_MODAL") as string;
  const fetchTilesView = useTilesViewAPI();
  const [tile, setTile] = useState<ITilesViewResponseData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isVisible || !tileName) return;
    setIsLoading(true);
    fetchTilesView(tileName)
      .then((res) => setTile(res || null))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, tileName]);

  const closeAndReset = () => {
    setTile(null);
    setInvisible();
  };

  return (
    <ModalPrompt
      modalId="TILE_VIEW_MODAL"
      contentOverflow={false}
      isLoading={isLoading}
      dialogClassName="sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
      button={{
        primaryButtonText: "Close",
        primaryButtonClassname: "bg-[#1a3c87] hover:bg-[#1a3c87]",
        secondaryButtonText: "",
        primaryCallback: closeAndReset,
        shouldHideOnClick: false,
      }}
      title={capitalizeFirstLetter(tile?.tile_name) || "Tile Details"}
      content={
        <div className="max-h-[50vh] overflow-y-auto pr-1">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : tile ? (
            <ViewTileModalContent tile={tile} />
          ) : (
            <p className="text-sm text-gray-500">No data found.</p>
          )}
        </div>
      }
    />
  );
};

export default ViewTileModal;
